import React, { KeyboardEvent, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Tooltip from 'flow/components/ui-components/Tooltip';
import Notification from 'flow/components/ui-components/Notification';
import { anyOptionalKeyDown } from 'flow/utils/browser-utils';

/**
 * What an arrow key does to the tab it moves to. `automatic` selects it,
 * for tabs whose content shows at once; `manual` only focuses it, and Enter
 * or Space selects it, for tabs that fetch, move focus or log a click.
 */
export type TabsActivation = 'automatic' | 'manual';

interface Props {
    selectedKey?: string | number;
    className?: string;
    tabs: TabV2Props[];
    onClick: (tab: TabV2Props) => void;
    /**
     * Let the pills grow to share the row width equally (each `flex-1`).
     * Combined with a `min-w-full` track this fills a wider container but
     * still falls back to natural widths (and scrolling) when crowded.
     */
    stretch?: boolean;
    activation?: TabsActivation;
    /**
     * The id of the element that shows the selected tab's content. The
     * caller gives it `role="tabpanel"` and `aria-labelledby={tabV2Id(...)}`.
     */
    panelId?: string;
}

export interface TabV2Props {
    key: string | number;
    label: string | React.JSX.Element;
    disabled?: boolean;
    tooltip?: string;
    badgeCounter?: number;
}

export const tabV2Id = (panelId: string, key: string | number) =>
    `${panelId}-tab-${key}`;

// Left and Right follow the reading direction and wrap at the ends.
const targetIndex = (
    key: string,
    from: number,
    count: number,
    isRtl: boolean,
): number | undefined => {
    const next = (from + 1) % count;
    const previous = (from - 1 + count) % count;
    switch (key) {
        case 'ArrowRight':
            return isRtl ? previous : next;
        case 'ArrowLeft':
            return isRtl ? next : previous;
        case 'Home':
            return 0;
        case 'End':
            return count - 1;
        default:
            return undefined;
    }
};

// The selected tab is the list's one Tab stop, else the first enabled tab.
const tabStopIndex = (tabs: TabV2Props[], selectedKey?: string | number) => {
    const selected = tabs.findIndex((tab) => tab.key === selectedKey);
    if (selected !== -1) return selected;
    return Math.max(
        tabs.findIndex((tab) => !tab.disabled),
        0,
    );
};

/**
 * A row of pill tabs, keyboard-operated as the WAI-ARIA tabs pattern: one
 * Tab stop, and Left, Right, Home and End move between the tabs. A disabled
 * tab takes focus but not selection, so its tooltip can say why it is off.
 */
const TabsV2 = ({
    selectedKey,
    tabs,
    onClick,
    className,
    stretch,
    activation = 'manual',
    panelId,
}: Props) => {
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const stopIndex = tabStopIndex(tabs, selectedKey);

    const onKeyDown = (
        event: KeyboardEvent<HTMLButtonElement>,
        from: number,
    ) => {
        if (anyOptionalKeyDown(event)) return;
        const isRtl = getComputedStyle(event.currentTarget).direction === 'rtl';
        const to = targetIndex(event.key, from, tabs.length, isRtl);
        if (to === undefined) return;
        event.preventDefault();
        tabRefs.current[to]?.focus();
        const tab = tabs[to];
        if (
            activation === 'automatic' &&
            !tab.disabled &&
            tab.key !== selectedKey
        ) {
            onClick(tab);
        }
    };

    return (
        <div role="tablist" className={twMerge('flex gap-2', className)}>
            {tabs.map((tab, index) => {
                const selected = tab.key === selectedKey;
                let stateClassName =
                    'bg-gray-200 text-gray-800 hover:bg-gray-300';
                if (tab.disabled) {
                    stateClassName =
                        'cursor-not-allowed bg-gray-200 text-gray-400';
                } else if (selected) {
                    stateClassName = 'bg-main font-bold text-white';
                }
                const button = (
                    <button
                        key={tab.key}
                        ref={(node) => {
                            tabRefs.current[index] = node;
                        }}
                        type="button"
                        role="tab"
                        id={panelId ? tabV2Id(panelId, tab.key) : undefined}
                        aria-selected={selected}
                        aria-controls={selected ? panelId : undefined}
                        aria-disabled={tab.disabled || undefined}
                        tabIndex={index === stopIndex ? 0 : -1}
                        onKeyDown={(event) => onKeyDown(event, index)}
                        onClick={() => {
                            if (!tab.disabled) onClick(tab);
                        }}
                        className={twMerge(
                            'focus-ring relative h-8 rounded-full px-5 text-xs antialiased transition-colors duration-200',
                            stretch && 'flex-1',
                            stateClassName,
                        )}
                    >
                        {/* Reserves the bold width, so selecting a tab does not
                            shift the row. */}
                        <span
                            className="invisible font-bold"
                            aria-hidden={true}
                        >
                            {tab.label}
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center">
                            {tab.label}
                        </span>
                        {tab.badgeCounter ? (
                            <Notification>{tab.badgeCounter}</Notification>
                        ) : null}
                    </button>
                );

                if (tab.tooltip) {
                    return (
                        <Tooltip key={tab.key} label={tab.tooltip}>
                            {button}
                        </Tooltip>
                    );
                }

                return button;
            })}
        </div>
    );
};

export default TabsV2;
